import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Input, Modal, message } from 'antd';
import { errorText } from '@/components/email-templates/useEmployeeNoticesT';
import { sendZaz } from '@/services/zaz.service';
import { EMPLOYEE_ZAZ_QUERY_KEY } from './zazKeys';

/**
 * HR sends the ZAZ to the heads of the employee's sector, with an optional
 * message. Shows who it goes to; without a head it cannot be sent.
 */
export default function ZazSendModal({
  open,
  onClose,
  employeeId,
  employeeName,
  heads = [],
}) {
  const { t } = useTranslation('zaz');
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const send = useMutation({
    mutationFn: () => sendZaz(employeeId, { note }),
    onSuccess: (result) => {
      if (result?.notified === false) message.warning(result?.message);
      else message.success(result?.message);
      queryClient.invalidateQueries({
        queryKey: [EMPLOYEE_ZAZ_QUERY_KEY, employeeId],
      });
      setNote('');
      onClose();
    },
    onError: (error) => message.error(errorText(error, t('actionFailed'))),
  });

  const hasHead = heads.length > 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('sendAction')}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button className="!rounded-[50px]" onClick={onClose}>
            {t('cancel', { ns: 'forms' })}
          </Button>
          <Button
            type="primary"
            className="!rounded-[50px]"
            disabled={!hasHead}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            {t('send')}
          </Button>
        </div>
      }
    >
      <p className="text-[13px] text-zinc-700">
        {t('sendIntro', { name: employeeName })}
      </p>
      {hasHead ? (
        <p className="text-[13px]">
          <span className="font-semibold">{t('sendTo')}:</span>{' '}
          {heads
            .map((head) =>
              head.hasEmail ? head.name : `${head.name} (${t('headNoEmail')})`
            )
            .join(', ')}
        </p>
      ) : (
        <Alert type="warning" showIcon message={t('noHead')} className="mb-3" />
      )}
      <div className="mb-1 text-[12.5px] font-semibold text-zinc-700">
        {t('noteLabel')}
      </div>
      <Input.TextArea
        value={note}
        onChange={(event) => setNote(event.target.value)}
        maxLength={1000}
        autoSize={{ minRows: 2, maxRows: 6 }}
        disabled={!hasHead}
      />
    </Modal>
  );
}
